import { useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import { ResumeInquiryFormData } from 'dashboard/forms/FormResumeInquiry';
import { SendInquiryEmailFormData } from 'dashboard/forms/FormSendInquiryEmail';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { reportIssueSuccessMessage } from 'dashboard/lib/constants/reportIssue';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Inquiry, InquiryReview, InquiryStatus } from 'dashboard/models/inquiry';
import { Verification } from 'dashboard/models/verification';
import { clearInquiry, setInquiry, setInquiryRedactor } from 'dashboard/redux/actions/inquiry';
import { showMessage } from 'dashboard/redux/actions/messages';
import { getSandboxEnvironments } from 'dashboard/redux/selectors/organization';
import { RootState } from 'dashboard/redux/store';
import {
  approveInquiry,
  declineInquiry,
  flagInquiry,
  loadInquiry,
  printInquiry,
  redactInquiry,
  resumeInquiry,
  sendInquiryEmail,
} from 'dashboard/redux/thunk-actions/inquiry';
import { loadUserList } from 'dashboard/redux/thunk-actions/user';
import { flagVerification } from 'dashboard/redux/thunk-actions/verification';
import DashboardInquiriesShowView from 'dashboard/views/DashboardInquiriesShowView';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useRedirectOnEnvironmentChange from 'lib/hooks/useRedirectOnEnvironmentChange';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { captureException, captureMessage } from 'lib/reporting';

const nullStateMessages: Record<number, string> = {
  401: "You don't have permissions to view this Inquiry.",
  404: 'We couldn’t find that Inquiry.',
};

type Params = { inquiryId: string };

type DashboardInquiriesShowRouteProps = PropsFromRedux;

const DashboardInquiriesShowRoute = (props: DashboardInquiriesShowRouteProps) => {
  useOnMountAndUnmount(props.clearInquiry);

  useRedirectOnEnvironmentChange('/dashboard/inquiries');

  const { inquiryId } = useParams<Params>() as Params; // inquiryId will always be defined

  const errorCode = useRef<number>(null);

  const [resumeCode, setResumeCode] = useState(undefined);

  useApiLoader({
    loader: () => props.loadUserList(),
    deps: [props.loadUserList],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load users.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: isLoadingInquiry, callback: loadInquiry } = useApiLoader({
    loader: () => props.loadInquiry(inquiryId, props.currentEnvironment.id),
    deps: [props.loadInquiry, inquiryId, props.currentEnvironment],
    onError: (err: ApiResponseError) => {
      if (isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else {
        props.showMessage('Failed to load inquiry.', MessageType.Failure);
      }

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      errorCode.current = err.status;
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: printInquiryError, callback: handlePrintInquiry } = useApiCallback(async () => {
    props.showMessage('Exporting Inquiry as PDF, please wait...', MessageType.Notice),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await props.printInquiry(props.inquiry.id);
  }, [props.inquiry, props.printInquiry]);

  useApiErrorHandler({
    error: printInquiryError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error instanceof ApiResponseError && error.status === 401) {
        props.showMessage(
          'Missing export inquiries permission to download as PDF.',
          MessageType.Failure,
        );
      } else {
        props.showMessage('Failed to download as PDF, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to download as PDF, please try again.', MessageType.Failure),
  });

  const { error: redactInquiryError, callback: handleRedactInquiry } = useApiCallback(async () => {
    if (props.inquiry == null) {
      return;
    }
    const inquiryResponse = await props.redactInquiry(props.inquiry.id);
    props.showMessage('Inquiry marked for redaction.', MessageType.Success);
    props.setInquiry(inquiryResponse.data);
    // Optimization to avoid reloading Inquiry since backend should set user as redactor.
    props.setInquiryRedactor(props.currentUser);
  }, [
    props.inquiry,
    props.redactInquiry,
    props.showMessage,
    props.setInquiry,
    props.setInquiryRedactor,
    props.currentUser,
    loadInquiry,
  ]);

  useApiErrorHandler({
    error: redactInquiryError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to redact inquiry, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to redact inquiry, please try again.', MessageType.Failure),
  });

  const { error: handleApproveInquiryError, callback: handleApproveInquiry } = useApiCallback(
    async (inquiryId: string, comment: string) => {
      const inquiryResponse = await props.approveInquiry(inquiryId, comment);
      props.setInquiry(inquiryResponse.data);
      props.showMessage('Successfully approved inquiry.', MessageType.Success);
    },
    [props.approveInquiry],
  );

  useApiErrorHandler({
    error: handleApproveInquiryError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error.status === 409) {
        props.showMessage('This inquiry has already been approved.', MessageType.Failure);
      } else {
        captureException(error, 'warning');
        props.showMessage('Failed to approve inquiry.', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage('Failed to approve inquiry.', MessageType.Failure),
  });

  const { error: handleDeclineInquiryError, callback: handleDeclineInquiry } = useApiCallback(
    async (inquiryId: string, comment: string) => {
      const inquiryResponse = await props.declineInquiry(inquiryId, comment);
      props.setInquiry(inquiryResponse.data);
      props.showMessage('Successfully declined inquiry.', MessageType.Success);
    },
    [props.declineInquiry],
  );

  useApiErrorHandler({
    error: handleDeclineInquiryError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error.status === 409) {
        props.showMessage('This inquiry has already been declined.', MessageType.Failure);
      } else {
        captureException(error, 'warning');
        props.showMessage('Failed to decline inquiry.', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage('Failed to decline inquiry.', MessageType.Failure),
  });

  const handleSubmitReview = async ({ comment, status }: InquiryReview) => {
    if (status === InquiryStatus.Approved) {
      handleApproveInquiry(inquiryId, comment);
    } else if (status === InquiryStatus.Declined) {
      handleDeclineInquiry(inquiryId, comment);
    } else {
      captureMessage('Invalid review action');
    }
  };

  const { callback: handleUpdateTags, error: handleUpdateTagsError } = useApiCallback(
    async ({ tags: tagIds }: FormUpdateTagsData) => {
      await SessionClientDashboard.postResourceAction<Inquiry>(
        {
          type: ApiModel.Inquiry,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          id: props.inquiry.id,
        },
        'set-tags',
        { meta: { 'tag-id': tagIds } },
      );

      await loadInquiry();
    },
    [props.inquiry, props.inquiryTags, loadInquiry],
  );

  useApiErrorHandler({
    error: handleUpdateTagsError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        captureException(error, 'warning');
        props.showMessage('Failed to add tags to inquiry.', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage('Failed to add tags to inquiry.', MessageType.Failure),
  });

  const { error: handleFlagIssuesError, callback: handleFlagIssues } = useApiCallback(
    async ({
      issueVerifications,
      issueType,
      responseType,
      content,
      addAsFraudulent,
    }: FlagIssuesFormData) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await props.flagInquiry(props.inquiry, props.verifications, {
        issueVerifications,
        issueType,
        responseType,
        content,
        addAsFraudulent,
      });
      props.showMessage(reportIssueSuccessMessage(responseType), MessageType.Success);
    },
    [props.inquiry, props.flagInquiry],
  );

  useApiErrorHandler({
    error: handleFlagIssuesError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to report issue, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to report issue, please try again.', MessageType.Failure),
  });

  const { error: handleFlagVerificationIssuesError, callback: handleFlagVerificationIssues } =
    useApiCallback(
      async (verification: Verification, values: FlagIssuesFormData) => {
        const { issueVerifications, issueType, responseType, content, addAsFraudulent } = values;
        await props.flagVerification(verification, {
          issueVerifications,
          issueType,
          responseType,
          content,
          addAsFraudulent,
        });
        props.showMessage(reportIssueSuccessMessage(responseType), MessageType.Success);
      },
      [props.flagVerification],
    );

  useApiErrorHandler({
    error: handleFlagVerificationIssuesError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to report issue, please try again.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Failed to report issue, please try again.', MessageType.Failure),
  });

  const { error: handleResumeInquiryError, callback: handleResumeInquiry } = useApiCallback(
    async (values: ResumeInquiryFormData) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const inquiryResponse = await props.resumeInquiry(props.inquiry.id, values);
      if (values?.sendEmailAddress && values?.emailAddress) {
        props.showMessage('Successfully sent email.', MessageType.Success);
      }
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setResumeCode(inquiryResponse.meta['one-time-link-code']);
      props.setInquiry(inquiryResponse.data);
    },
    [props.resumeInquiry, props.inquiry],
  );

  useApiErrorHandler({
    error: handleResumeInquiryError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to resume inquiry.', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage('Failed to resume inquiry.', MessageType.Failure),
  });

  const { error: handleSendInquiryEmailError, callback: handleSendInquiryEmail } = useApiCallback(
    async (values: SendInquiryEmailFormData) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const inquiryResponse = await props.sendInquiryEmail(props.inquiry.id, values);
      props.setInquiry(inquiryResponse.data);
      props.showMessage('Successfully sent email.', MessageType.Success);
    },
    [props.sendInquiryEmail, props.inquiry],
  );
  useApiErrorHandler({
    error: handleSendInquiryEmailError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to send email.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues.', MessageType.Failure),
  });

  return (
    <DashboardInquiriesShowView
      creator={props.creator}
      cases={props.cases}
      devices={props.devices}
      documents={props.documents}
      featureFlags={props.featureFlags}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      inquiry={props.inquiry}
      inquiryTags={props.inquiryTags}
      inquiryTemplateVersion={props.inquiryTemplateVersion}
      lists={props.lists}
      listItems={props.listItems}
      listMatches={props.listMatches}
      isLoadingInquiry={isLoadingInquiry}
      redactor={props.redactor}
      reviewer={props.reviewer}
      reviewerWorkflow={props.reviewerWorkflow}
      reviewerWorkflowRun={props.reviewerWorkflowRun}
      reviewerWorkflowVersion={props.reviewerWorkflowVersion}
      reports={props.reports}
      selfies={props.selfies}
      sessions={props.sessions}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      template={props.template}
      verifications={props.verifications}
      users={props.users}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      resumeCode={resumeCode}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setResumeCode={setResumeCode}
      handleSubmitReview={handleSubmitReview}
      handlePrintInquiry={handlePrintInquiry}
      handleFlagIssues={handleFlagIssues}
      handleFlagVerificationIssues={handleFlagVerificationIssues}
      resumeInquiry={handleResumeInquiry}
      redactInquiry={handleRedactInquiry}
      handleUpdateTags={handleUpdateTags}
      sendInquiryEmail={handleSendInquiryEmail}
      organization={props.organization}
      currentEnvironment={props.currentEnvironment}
      matchUrl={location.pathname}
      showMessage={props.showMessage}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      nullStateMessage={nullStateMessages[errorCode.current]}
      currentUser={props.currentUser}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    cases: state.inquiry.cases,
    devices: state.inquiry.devices,
    documents: state.inquiry.documents,
    featureFlags: state.featureFlags.featureFlags,
    inquiry: state.inquiry.inquiry,
    inquiryTags: state.inquiry.tags,
    inquiryTemplateVersion: state.inquiry.inquiryTemplateVersion,
    lists: state.inquiry.lists,
    listItems: state.inquiry.listItems,
    listMatches: state.inquiry.listMatches,
    creator: state.inquiry.creator,
    redactor: state.inquiry.redactor,
    reviewer: state.inquiry.reviewer,
    reviewerWorkflow: state.inquiry.reviewerWorkflow,
    reviewerWorkflowRun: state.inquiry.reviewerWorkflowRun,
    reviewerWorkflowVersion: state.inquiry.reviewerWorkflowVersion,
    reports: state.inquiry.reports,
    selfies: state.inquiry.selfies,
    sessions: state.inquiry.sessions,
    template: state.inquiry.template,
    verifications: state.inquiry.verifications,
    users: state.user.users,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    sandboxEnvironments: getSandboxEnvironments(state),
    organization: state.organization.organization,
    currentUser: state.currentUser.currentUser,
  };
};

const mapDispatchToProps = {
  clearInquiry,
  setInquiry,
  setInquiryRedactor,
  loadInquiry,
  printInquiry,
  redactInquiry,
  approveInquiry,
  declineInquiry,
  flagInquiry,
  flagVerification,
  resumeInquiry,
  sendInquiryEmail,
  loadUserList,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardInquiriesShowRoute);
